import { apiClient } from '../api-client';
import { ApiResponse, isApiError } from '../../dtos/common/api.dto';
import {
  Authorization,
  AuthorizationDocument,
  AuthorizationStatus,
  AuthorizationDiscipline,
  ApiAuthorizationDiscipline,
  PayerType,
  PlaceOfService,
  QuantityType,
  MedicalSpecialty,
  HCPCS_BY_DISCIPLINE,
  PAStatus,
  PAPayerType,
  MedicaidProgram,
  ServiceCategory,
  PARequestType,
  PAUnitType,
  PAServiceType,
  PAApprovalRequest,
  PACreateRequest,
  PAUsageResponse,
  PADocument,
  PADocumentConfirmRequest,
  PASubmitOptions,
  PASubmitPreflight,
  PASubmitWarning,
  PAPacketResponse,
  PAMcoOption,
  PAFlowSheetError
} from '../../dtos/patientmr/authorizations.dto';
import { InsuranceService } from './insurance.service';
import { PresignedUploadService, UploadTicket, resolveContentType, ticketRequestFor } from '../uploads/presignedUpload';
import { getDisciplineColors, paCategoryDiscipline, paCategoryEntries } from '../../constants/disciplines';

// Re-export types, enums and constants for backward compatibility
export type {
  Authorization, AuthorizationDocument, PAApprovalRequest, PACreateRequest, PAUsageResponse, PADocument,
  PASubmitPreflight, PASubmitWarning, PAPacketResponse, PAMcoOption, PAFlowSheetError
};

/** Statuses a PA can be copied from (PA-06 / RCT-02, Phase 8 §3.12). */
export const PA_COPYABLE_STATUSES: ReadonlySet<string> = new Set([
  PAStatus.APPROVED, PAStatus.EXPIRED, PAStatus.EXHAUSTED, PAStatus.DENIED
]);

/** Document actions (upload confirm is allowed more widely; delete only while DRAFT/PENDED). */
export const PA_DOCUMENT_DELETE_STATUSES: ReadonlySet<string> = new Set([PAStatus.DRAFT, PAStatus.PENDED]);

/** Raw document row as the documents routes return it (field names vary across versions). */
interface RawPADocument {
  documentId?: string;
  id?: string;
  documentName?: string;
  fileName?: string;
  name?: string;
  fileSize?: number;
  size?: number;
  contentType?: string;
  fileType?: string;
  key?: string;
  s3Key?: string;
  uploadedAt?: string;
  uploadedBy?: string;
  deletedAt?: string | null;
  deleted?: boolean;
}

function toPADocument(raw: RawPADocument): PADocument | null {
  const id = raw.documentId || raw.id;
  if (!id || raw.deleted === true || raw.deletedAt) return null;
  return {
    id,
    fileName: raw.documentName || raw.fileName || raw.name || 'Document',
    fileSize: raw.fileSize ?? raw.size ?? 0,
    fileType: raw.contentType || raw.fileType || '',
    s3Key: raw.key || raw.s3Key || '',
    uploadedAt: raw.uploadedAt || '',
    uploadedBy: raw.uploadedBy || ''
  };
}

/** Pulls PA documents out of any response shape: a list, {documents}, {document}, or a single row. */
function extractPADocuments(data: unknown): PADocument[] {
  if (!data || typeof data !== 'object') return [];
  const rows: unknown[] = Array.isArray(data)
    ? data
    : Array.isArray((data as { documents?: unknown }).documents)
      ? (data as { documents: unknown[] }).documents
      : (data as { document?: unknown }).document
        ? [(data as { document: unknown }).document]
        : [data];
  return rows
    .filter((r): r is RawPADocument => !!r && typeof r === 'object')
    .map(toPADocument)
    .filter((d): d is PADocument => d !== null);
}

const mcoCache = new Map<string, Promise<PAMcoOption[]>>();

export interface PAEvvDriftResponse {
  evvDrift: {
    /** Same value as peloraConsumedUnits (kept for older backends) */
    blackbirdConsumedUnits: number;
    peloraConsumedUnits?: number;
    evvVerifiedUnits: number;
    pendingReconciliationUnits?: number;
    driftUnits: number;
    linkedVisitCount?: number;
    verifiedVisitCount?: number;
  };
}
export {
  AuthorizationStatus,
  PayerType,
  PlaceOfService,
  QuantityType,
  MedicalSpecialty,
  PAStatus,
  PAPayerType,
  MedicaidProgram,
  ServiceCategory,
  PARequestType,
  PAUnitType,
  PAServiceType,
  /** PA form prefill only (see the DTO) */
  HCPCS_BY_DISCIPLINE
};

// MCO lists come from systemConfig.payers.medicaidMcos (Phase 8 §3.1, R-180): see getMedicaidMcos.

// Service category configuration
// Phase 20A: from the discipline registry (sn_visit added; HHA per visit with no RN/LVN mix, D-7)
export const SERVICE_CATEGORY_CONFIG: Record<string, { label: string; defaultUnitType: PAUnitType; defaultMinutesPerUnit: number | null; showServiceType: boolean }> = Object.fromEntries(
  paCategoryEntries().map(c => [c.code, { label: c.label, defaultUnitType: c.unitType as PAUnitType, defaultMinutesPerUnit: c.minutesPerUnit, showServiceType: c.showServiceType }])
);

// Re-export discipline enums from DTO for backward compatibility
export { AuthorizationDiscipline, ApiAuthorizationDiscipline };

// Removed: AuthorizationDocument interface (now in DTO)
// Removed: Authorization interface (now in DTO)

// Authorization list response interface (without double-nesting)
export interface AuthorizationListResponseData {
  authorizations: Authorization[];
  count: number;
}

// Authorization create/update request interface
export interface AuthorizationRequest {
  discipline: AuthorizationDiscipline;
  startDate: string;
  expirationDate: string;
  authNumber: string;
  authorizedVisits: number;
  quantityType?: QuantityType;
  timePerUnit?: string;
  hcpcsCode?: string;
  notes: string;
  status?: AuthorizationStatus;
  authorizationType?: string;
  insuranceProvider?: string | null;

  // Link to POC Certification
  pocCertificationId?: string;

  // Usage Tracking
  unitsUsed?: number;
  lastClaimDate?: string;

  // Payor Contact Info
  payorName?: string;
  payorPhone?: string;
  caseManagerName?: string;
  caseManagerPhone?: string;

  // Denial/Appeal Tracking
  denialReason?: string;
  appealDate?: string;
  appealOutcome?: string;

  // Document attachments
  documents?: AuthorizationDocument[];

  // PA Module fields (optional — present only on PA-type authorizations)
  payerType?: string | null;
  medicaidProgram?: string | null;
  payerName?: string | null;
  serviceCategory?: string | null;
  requestType?: string | null;
  unitType?: string | null;
  minutesPerUnit?: number | null;
  requestedStartDate?: string | null;
  requestedEndDate?: string | null;
  revisionReason?: string | null;
  linkedFormId?: string | null;
  linkedFormType?: 'CCP_PA_REQUEST' | 'CCP_PA_REAUTH' | 'TX_THERAPIES_PA' | null;
  therapyAcuity?: 'ACUTE' | 'CHRONIC' | null;
  attendantProgram?: 'PCS' | 'CFC_PAS' | 'CFC_HAB' | null;
  linkedFlowSheetFormId?: string | null;
}

// Document response interface (without double-nesting)
export interface DocumentSignedUrlData {
  documentId: string;
  documentName: string;
  contentType: string;
  uploadedAt: string;
  uploadedBy: string;
  signedUrl: string;
}

/**
 * Service for managing patient authorizations with backend API
 */
export class AuthorizationsService {
  /**
   * Map UI discipline to API discipline
   * @param uiDiscipline - The UI-facing discipline enum value
   * @returns API-facing discipline enum value
   */
  static mapUiDisciplineToApiDiscipline(uiDiscipline: AuthorizationDiscipline): ApiAuthorizationDiscipline {
    switch (uiDiscipline) {
      case AuthorizationDiscipline.RN:
        return ApiAuthorizationDiscipline.RN;
      case AuthorizationDiscipline.PT:
        return ApiAuthorizationDiscipline.PT;
      case AuthorizationDiscipline.OT:
        return ApiAuthorizationDiscipline.OT;
      case AuthorizationDiscipline.ST:
        return ApiAuthorizationDiscipline.ST;
      case AuthorizationDiscipline.MSW:
        return ApiAuthorizationDiscipline.MSW;
      case AuthorizationDiscipline.HHA:
        return ApiAuthorizationDiscipline.HHA;
      default:
        return ApiAuthorizationDiscipline.RN; // Default fallback
    }
  }

  /**
   * Map API discipline to UI discipline
   * @param apiDiscipline - The API-facing discipline enum value
   * @returns UI-facing discipline enum value
   */
  static mapApiDisciplineToUiDiscipline(apiDiscipline: ApiAuthorizationDiscipline | string): AuthorizationDiscipline {
    switch (apiDiscipline) {
      case ApiAuthorizationDiscipline.RN:
        return AuthorizationDiscipline.RN;
      case ApiAuthorizationDiscipline.PT:
      case 'PHYSICAL_THERAPY':
        return AuthorizationDiscipline.PT;
      case ApiAuthorizationDiscipline.OT:
      case 'OCCUPATIONAL_THERAPY':
        return AuthorizationDiscipline.OT;
      case ApiAuthorizationDiscipline.ST:
      case 'SPEECH_THERAPY':
        return AuthorizationDiscipline.ST;
      case ApiAuthorizationDiscipline.MSW:
      case 'SOCIAL_WORK':
        return AuthorizationDiscipline.MSW;
      case ApiAuthorizationDiscipline.HHA:
        return AuthorizationDiscipline.HHA;
      default:
        return AuthorizationDiscipline.RN; // Default fallback
    }
  }

  /**
   * Map API status to UI status
   * @param apiStatus - The API-facing status value
   * @returns UI-facing status enum value
   */
  static mapApiStatusToUiStatus(apiStatus: string): AuthorizationStatus {
    const upperApiStatus = apiStatus.toUpperCase();

    switch (upperApiStatus) {
      case 'ACTIVE':
        return AuthorizationStatus.ACTIVE;
      case 'EXPIRED':
        return AuthorizationStatus.EXPIRED;
      case 'PENDING':
        return AuthorizationStatus.PENDING;
      case 'CANCELLED':
      case 'CANCELED':
        return AuthorizationStatus.CANCELLED;
      default:
        return AuthorizationStatus.ACTIVE; // Default fallback
    }
  }

  /**
   * Map UI status to API status
   * @param uiStatus - The UI-facing status enum value
   * @returns API-facing status string
   */
  static mapUiStatusToApiStatus(uiStatus: AuthorizationStatus): string {
    switch (uiStatus) {
      case AuthorizationStatus.ACTIVE:
        return 'ACTIVE';
      case AuthorizationStatus.EXPIRED:
        return 'EXPIRED';
      case AuthorizationStatus.PENDING:
        return 'PENDING';
      case AuthorizationStatus.CANCELLED:
        return 'CANCELLED';
      default:
        return 'ACTIVE'; // Default fallback
    }
  }

  /**
   * Helper function to format authorization data from the API response
   * @param authorization Authorization data from the API
   * @returns Formatted Authorization object
   */
  static formatAuthorization(authorization: any): Authorization {
    // Map API discipline to UI discipline if it exists
    const discipline = authorization.discipline ?
      this.mapApiDisciplineToUiDiscipline(authorization.discipline as ApiAuthorizationDiscipline) :
      AuthorizationDiscipline.RN;

    // Determine if this is a PA module record
    const isPARecord = authorization.serviceCategory || authorization.payerType;

    // For PA records, use the status as-is; for legacy, map it
    const status = isPARecord
      ? (authorization.status || PAStatus.DRAFT)
      : (authorization.status ? this.mapApiStatusToUiStatus(authorization.status) : AuthorizationStatus.ACTIVE);

    return {
      id: authorization.authorizationId || authorization.id,
      authorizationId: authorization.authorizationId,
      paNumber: authorization.paNumber || null,
      discipline,
      startDate: authorization.startDate || '',
      expirationDate: authorization.expirationDate || '',
      documents: authorization.documents || [],
      status,
      authNumber: authorization.authorizationNumber || authorization.authNumber || '',
      visits: Math.max(0, typeof authorization.authorizedVisits === 'string'
        ? parseInt(authorization.authorizedVisits, 10) || 0
        : authorization.authorizedVisits ?? authorization.visits ?? 0),
      authorizedVisits: Math.max(0, typeof authorization.authorizedVisits === 'string'
        ? parseInt(authorization.authorizedVisits, 10) || 0
        : authorization.authorizedVisits ?? authorization.visits ?? 0),
      quantityType: authorization.quantityType || QuantityType.VISITS,
      timePerUnit: authorization.timePerUnit || undefined,
      hcpcsCode: authorization.hcpcsCode || undefined,
      notes: authorization.notes || '',
      authorizationType: authorization.authorizationType || undefined,
      usedVisits: authorization.usedVisits || 0,
      remainingVisits: authorization.remainingVisits || 0,
      insuranceProvider: authorization.insuranceProvider || null,
      createdAt: authorization.createdAt || undefined,
      updatedAt: authorization.updatedAt || undefined,

      // POC Certification link
      pocCertificationId: authorization.pocCertificationId || undefined,
      pocCertificationStatus: authorization.pocCertificationStatus || undefined,

      // Usage tracking
      unitsUsed: authorization.unitsUsed || 0,
      unitsRemaining: authorization.unitsRemaining ||
        (authorization.authorizedVisits || authorization.visits || 0) - (authorization.unitsUsed || 0),
      lastClaimDate: authorization.lastClaimDate || undefined,

      // Payor contact
      payorName: authorization.payorName || undefined,
      payorPhone: authorization.payorPhone || undefined,
      caseManagerName: authorization.caseManagerName || undefined,
      caseManagerPhone: authorization.caseManagerPhone || undefined,

      // Denial/appeal tracking
      denialReason: authorization.denialReason || undefined,
      appealDate: authorization.appealDate || undefined,
      appealOutcome: authorization.appealOutcome || undefined,

      // PA Module fields
      payerType: authorization.payerType || null,
      medicaidProgram: authorization.medicaidProgram || null,
      payerName: authorization.payerName || null,
      serviceCategory: authorization.serviceCategory || null,
      requestType: authorization.requestType || null,
      unitType: authorization.unitType || null,
      minutesPerUnit: authorization.minutesPerUnit || null,
      // Fall back to legacy startDate/expirationDate for records created before PA module
      requestedStartDate: authorization.requestedStartDate || authorization.startDate || null,
      requestedEndDate: authorization.requestedEndDate || authorization.expirationDate || null,
      revisionReason: authorization.revisionReason || null,
      approvedStartDate: authorization.approvedStartDate || null,
      approvedEndDate: authorization.approvedEndDate || null,
      approvedQuantity: authorization.approvedQuantity || null,
      approvedPerWeek: authorization.approvedPerWeek || null,
      serviceType: authorization.serviceType || null,
      uaApproved: authorization.uaApproved === true,
      therapyAcuity: authorization.therapyAcuity || null,
      attendantProgram: authorization.attendantProgram || null,
      usedQuantity: authorization.usedQuantity || 0,
      documentCount: authorization.documentCount || (authorization.documents?.length ?? 0),
      linkedFormId: authorization.linkedFormId || null,
      linkedFormType: authorization.linkedFormType || null,
      linkedFlowSheetFormId: authorization.linkedFlowSheetFormId || null,
      expirationAlertLevel: authorization.expirationAlertLevel || undefined,
      expirationDaysRemaining: authorization.expirationDaysRemaining ?? undefined,
      deadlines: authorization.deadlines || null,
      notice: typeof authorization.notice === 'string' && authorization.notice ? authorization.notice : null
    };
  }

  /**
   * Helper function to format an array of authorizations from the API
   * @param authorizations Array of authorizations from the API
   * @returns Array of formatted Authorization objects
   */
  static formatAuthorizationList(authorizations: any[]): Authorization[] {
    return authorizations.map(auth => this.formatAuthorization(auth));
  }

  /**
   * Fetch all authorizations for a patient
   * @param patientId - The ID of the patient
   * @returns Promise with the list of authorizations
   */
  static async getAuthorizations(patientId: string): Promise<Authorization[]> {
    try {
      const response: ApiResponse<AuthorizationListResponseData> = await apiClient.get(
        `/patientmr/${patientId}/authorization`
      );

      if (response.success && response.data) {
        const authorizations = response.data.authorizations || [];
        return this.formatAuthorizationList(authorizations);
      }

      throw new Error(response.error?.message || 'Failed to fetch authorizations');
    } catch (error) {
      console.error(`Error fetching authorizations for patient ${patientId}:`, error);
      throw error;
    }
  }

  /**
   * Get a specific authorization's details
   * @param patientId - The ID of the patient
   * @param authorizationId - The ID of the authorization
   * @returns Promise with the authorization details
   */
  static async getAuthorizationDetails(patientId: string, authorizationId: number | string): Promise<Authorization> {
    try {
      const response: ApiResponse<any> = await apiClient.get(
        `/patientmr/${patientId}/authorization/${authorizationId}`
      );

      if (response.success && response.data) {
        return this.formatAuthorization(response.data);
      }

      throw new Error(response.error?.message || 'Failed to fetch authorization details');
    } catch (error) {
      console.error(`Error fetching authorization details for patient ${patientId}, authorization ${authorizationId}:`, error);
      throw error;
    }
  }

  /**
   * Create a new authorization
   * @param patientId - The ID of the patient
   * @param authorizationData - The authorization data to create
   * @returns Promise with the created authorization
   */
  static async createAuthorization(
    patientId: string,
    authorizationData: AuthorizationRequest
  ): Promise<Authorization> {
    try {
      // Convert UI values to API values and map field names to match what API expects
      const apiData = {
        ...authorizationData,
        discipline: this.mapUiDisciplineToApiDiscipline(authorizationData.discipline),
        authorizationNumber: authorizationData.authNumber,
        status: authorizationData.status ? this.mapUiStatusToApiStatus(authorizationData.status) : undefined
      };

      const response: ApiResponse<any> = await apiClient.post(
        `/patientmr/${patientId}/authorization`,
        apiData
      );

      if (response.success && response.data) {
        return this.formatAuthorization(response.data);
      }

      throw new Error(response.error?.message || 'Failed to create authorization');
    } catch (error) {
      console.error(`Error creating authorization for patient ${patientId}:`, error);
      throw error;
    }
  }

  /**
   * Update an existing authorization
   * @param patientId - The ID of the patient
   * @param authorizationId - The ID of the authorization
   * @param authorizationData - The authorization data to update
   * @returns Promise with the updated authorization
   */
  static async updateAuthorization(
    patientId: string,
    authorizationId: number | string,
    authorizationData: AuthorizationRequest
  ): Promise<Authorization> {
    try {
      // Phase 4: status changes only go through the submit/approve/deny/pend actions (the update
      // route rejects a body with `status`), and authNumber/usedVisits are no longer
      // client-editable, so none of them are sent on update.
      // Phase 8 (R-128): documents change only through the documents routes, never the update.
      const {
        status: _status,
        authNumber: _authNumber,
        usedVisits: _usedVisits,
        documents: _documents,
        ...editable
      } = authorizationData as AuthorizationRequest & { status?: unknown; usedVisits?: unknown };
      const apiData = {
        ...editable,
        discipline: this.mapUiDisciplineToApiDiscipline(authorizationData.discipline)
      };

      const response: ApiResponse<any> = await apiClient.put(
        `/patientmr/${patientId}/authorization/${authorizationId}`,
        apiData
      );

      if (response.success && response.data) {
        return this.formatAuthorization(response.data);
      }

      throw new Error(response.error?.message || 'Failed to update authorization');
    } catch (error) {
      console.error(`Error updating authorization ${authorizationId} for patient ${patientId}:`, error);
      throw error;
    }
  }

  /**
   * Get a signed URL for downloading a document
   * @param patientId - The ID of the patient
   * @param authorizationId - The ID of the authorization
   * @param documentId - The ID of the document
   * @returns Promise with the document details including signed URL
   */
  static async getDocumentSignedUrl(
    patientId: string,
    authorizationId: string | number,
    documentId: string
  ): Promise<DocumentSignedUrlData> {
    try {
      const response: ApiResponse<DocumentSignedUrlData> = await apiClient.get(
        `/patientmr/${patientId}/authorization/${authorizationId}/documents/${documentId}`
      );

      if (response.success && response.data) {
        return response.data;
      }

      throw new Error(response.error?.message || 'Failed to get document signed URL');
    } catch (error) {
      console.error(`Error getting document signed URL:`, error);
      throw error;
    }
  }

  /**
   * Delete an authorization
   * @param patientId - The ID of the patient
   * @param authorizationId - The ID of the authorization to delete
   * @returns Promise indicating success
   */
  static async deleteAuthorization(patientId: string, authorizationId: number | string, reason?: string): Promise<void> {
    try {
      // Soft delete, DRAFT only (409 RECORD_LOCKED otherwise). Response: {authorizationId, deleted, deletedAt}
      const response: ApiResponse<{ authorizationId?: string; deleted?: boolean; deletedAt?: string }> = await apiClient.delete(
        `/patientmr/${patientId}/authorization/${authorizationId}/delete`,
        reason ? { reason } : undefined
      );

      if (response.success) {
        return;
      }

      throw new Error(response.error?.message || 'Failed to delete authorization');
    } catch (error) {
      console.error(`Error deleting authorization ${authorizationId} for patient ${patientId}:`, error);
      throw error;
    }
  }

  /**
   * Calculate status based on dates
   * @param startDate - The start date string
   * @param expirationDate - The expiration date string
   * @returns The calculated status
   */
  static calculateStatus(startDate: string, expirationDate: string): AuthorizationStatus {
    const now = new Date();
    const start = new Date(startDate);
    const expiration = new Date(expirationDate);

    if (now < start) return AuthorizationStatus.PENDING;
    if (now > expiration) return AuthorizationStatus.EXPIRED;
    return AuthorizationStatus.ACTIVE;
  }

  /**
   * Format date for display
   * @param dateString - The date string to format
   * @returns Formatted date string
   */
  static formatDate(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  /**
   * Get status badge styles
   * @param status - The authorization status
   * @returns CSS classes for status badge
   */
  static getStatusBadgeStyles(status: AuthorizationStatus): string {
    switch (status) {
      case AuthorizationStatus.ACTIVE:
        return 'bg-green-100 text-green-800';
      case AuthorizationStatus.EXPIRED:
        return 'bg-red-100 text-red-800';
      case AuthorizationStatus.PENDING:
        return 'bg-yellow-100 text-yellow-800';
      case AuthorizationStatus.CANCELLED:
        return 'bg-slate-100 text-slate-800';
      default:
        return 'bg-slate-100 text-slate-800';
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PA Module Methods
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Create a new PA request (defaults to DRAFT status)
   */
  static async createPA(patientId: string, data: PACreateRequest): Promise<Authorization> {
    try {
      // Map serviceCategory to legacy discipline field for backend compatibility

      const apiData = {
        ...data,
        discipline: paCategoryDiscipline(data.serviceCategory) || 'RN',
        startDate: data.requestedStartDate,
        expirationDate: data.requestedEndDate,
        authorizedVisits: 0,
        status: 'DRAFT'
      };

      const response: ApiResponse<any> = await apiClient.post(
        `/patientmr/${patientId}/authorization`,
        apiData
      );

      if (response.success && response.data) {
        return this.formatAuthorization(response.data);
      }

      throw new Error(response.error?.message || 'Failed to create PA request');
    } catch (error) {
      console.error(`Error creating PA for patient ${patientId}:`, error);
      throw error;
    }
  }

  /**
   * Submit a PA (DRAFT/PENDED → SUBMITTED)
   */
  static async submitPA(
    patientId: string,
    authorizationId: string,
    options: Pick<PASubmitOptions, 'acknowledgeWarnings'> = {}
  ): Promise<Authorization> {
    try {
      // 422 FLOWSHEET_INCOMPLETE carries `errors: [{day, time?, code}]` in ApiError.details
      const response: ApiResponse<any> = await apiClient.post(
        `/patientmr/${patientId}/authorization/${authorizationId}/submit`,
        options.acknowledgeWarnings ? { acknowledgeWarnings: true } : undefined
      );

      if (response.success && response.data) {
        return this.formatAuthorization(response.data);
      }

      throw new Error(response.error?.message || 'Failed to submit PA');
    } catch (error) {
      console.error(`Error submitting PA ${authorizationId}`);
      throw error;
    }
  }

  /**
   * Submit preflight (PA-13): returns the non-blocking warnings without changing status.
   * Hard errors (e.g. FLOWSHEET_INCOMPLETE) are thrown as ApiError like a real submit.
   */
  static async preflightSubmit(patientId: string, authorizationId: string): Promise<PASubmitPreflight> {
    const response: ApiResponse<{ warnings?: PASubmitWarning[] }> = await apiClient.post(
      `/patientmr/${patientId}/authorization/${authorizationId}/submit`,
      { dryRun: true }
    );
    if (response.success) {
      const warnings = Array.isArray(response.data?.warnings) ? response.data!.warnings! : [];
      return {
        warnings: warnings.filter(w => w && typeof w.message === 'string')
      };
    }
    throw new Error(response.error?.message || 'Failed to check PA before submit');
  }

  /**
   * Approve a PA (SUBMITTED → APPROVED)
   */
  static async approvePA(
    patientId: string,
    authorizationId: string,
    approvalData: PAApprovalRequest
  ): Promise<Authorization> {
    try {
      // Dedicated action route (the generic update rejects `status`)
      const response: ApiResponse<any> = await apiClient.post(
        `/patientmr/${patientId}/authorization/${authorizationId}/approve`,
        {
          authNumber: approvalData.authNumber,
          approvedStartDate: approvalData.approvedStartDate,
          approvedEndDate: approvalData.approvedEndDate,
          approvedQuantity: approvalData.approvedQuantity,
          approvedPerWeek: approvalData.approvedPerWeek,
          serviceType: approvalData.serviceType ?? null,
          uaApproved: approvalData.uaApproved === true
        }
      );

      if (response.success && response.data) {
        // PA-03: approval past the end date saves EXPIRED and returns `notice` (on the PA or beside it)
        const data = response.data;
        const record = data.authorization && typeof data.authorization === 'object' ? data.authorization : data;
        const notice = typeof data.notice === 'string' ? data.notice : record.notice;
        return this.formatAuthorization({ ...record, notice });
      }

      throw new Error(response.error?.message || 'Failed to approve PA');
    } catch (error) {
      console.error(`Error approving PA ${authorizationId}:`, error);
      throw error;
    }
  }

  /**
   * Deny a PA (SUBMITTED → DENIED)
   */
  static async denyPA(patientId: string, authorizationId: string): Promise<Authorization> {
    try {
      const response: ApiResponse<any> = await apiClient.post(
        `/patientmr/${patientId}/authorization/${authorizationId}/deny`
      );

      if (response.success && response.data) {
        return this.formatAuthorization(response.data);
      }

      throw new Error(response.error?.message || 'Failed to deny PA');
    } catch (error) {
      console.error(`Error denying PA ${authorizationId}:`, error);
      throw error;
    }
  }

  /**
   * Pend a PA (SUBMITTED → PENDED)
   */
  static async pendPA(patientId: string, authorizationId: string): Promise<Authorization> {
    try {
      const response: ApiResponse<any> = await apiClient.post(
        `/patientmr/${patientId}/authorization/${authorizationId}/pend`
      );

      if (response.success && response.data) {
        return this.formatAuthorization(response.data);
      }

      throw new Error(response.error?.message || 'Failed to pend PA');
    } catch (error) {
      console.error(`Error pending PA ${authorizationId}:`, error);
      throw error;
    }
  }

  /**
   * Copy an APPROVED/EXPIRED/EXHAUSTED/DENIED PA to a new DRAFT (PA-06; linked F00120 is cloned server side)
   */
  static async copyPA(patientId: string, authorizationId: string): Promise<Authorization> {
    try {
      const response: ApiResponse<any> = await apiClient.post(
        `/patientmr/${patientId}/authorization/${authorizationId}/copy`
      );

      if (response.success && response.data) {
        return this.formatAuthorization(response.data);
      }

      throw new Error(response.error?.message || 'Failed to copy PA');
    } catch (error) {
      console.error(`Error copying PA ${authorizationId}:`, error);
      throw error;
    }
  }

  /**
   * Get usage data (linked visits + summary) for an authorization
   */
  static async getUsage(patientId: string, authorizationId: string): Promise<PAUsageResponse> {
    try {
      const response: ApiResponse<PAUsageResponse> = await apiClient.get(
        `/patientmr/${patientId}/authorization/${authorizationId}/usage`
      );

      if (response.success && response.data) {
        return response.data;
      }

      throw new Error(response.error?.message || 'Failed to get usage data');
    } catch (error) {
      console.error(`Error fetching usage for PA ${authorizationId}:`, error);
      throw error;
    }
  }

  static async getEvvDrift(patientId: string, authorizationId: string): Promise<PAEvvDriftResponse> {
    const response: ApiResponse<PAEvvDriftResponse> = await apiClient.get(
      `/patientmr/${patientId}/authorizations/${authorizationId}/evv-drift`
    );
    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to get EVV drift');
    }
    return response.data;
  }

  // ── Documents (Phase 8 §3.4: pre-signed S3 POST + confirm) ───────────────

  /** Ticket for a pre-signed S3 POST of one PA document. */
  static async getDocumentUploadTicket(
    patientId: string,
    authorizationId: string,
    request: { fileName: string; contentType: string; size: number }
  ): Promise<UploadTicket> {
    const response: ApiResponse<UploadTicket> = await apiClient.post(
      `/patientmr/${patientId}/authorization/${authorizationId}/documents/upload-url`,
      request
    );
    if (response.success && response.data?.uploadUrl) return response.data;
    throw new Error(response.error?.message || 'Could not start the upload');
  }

  /** Confirm an uploaded object (server checks size and file type; 422 deletes it). */
  static async confirmDocument(
    patientId: string,
    authorizationId: string,
    body: PADocumentConfirmRequest
  ): Promise<PADocument | null> {
    const response: ApiResponse<unknown> = await apiClient.post(
      `/patientmr/${patientId}/authorization/${authorizationId}/documents`,
      body
    );
    if (response.success) {
      const docs = extractPADocuments(response.data);
      return docs.find(d => d.s3Key === body.key) || docs[docs.length - 1] || null;
    }
    throw new Error(response.error?.message || 'Could not save the uploaded document');
  }

  /** Current (not deleted) documents on a PA. */
  static async listDocuments(patientId: string, authorizationId: string): Promise<PADocument[]> {
    const response: ApiResponse<unknown> = await apiClient.get(
      `/patientmr/${patientId}/authorization/${authorizationId}/documents`
    );
    if (response.success) return extractPADocuments(response.data);
    throw new Error(response.error?.message || 'Failed to load documents');
  }

  /**
   * Upload one document: client checks (PDF/JPG/PNG ≤ 10 MB) → ticket → S3 POST with real
   * progress → confirm. Returns the saved document (null when the confirm response has no row).
   */
  static async uploadDocument(
    patientId: string,
    authorizationId: string,
    file: File,
    onProgress?: (pct: number) => void,
    documentType: string = 'PA_SUPPORTING'
  ): Promise<PADocument | null> {
    const { key } = await PresignedUploadService.upload(
      file,
      () => AuthorizationsService.getDocumentUploadTicket(patientId, authorizationId, ticketRequestFor(file)),
      onProgress
    );
    return AuthorizationsService.confirmDocument(patientId, authorizationId, {
      key,
      documentName: file.name,
      documentType: documentType || resolveContentType(file)
    });
  }

  /**
   * Delete a document from an authorization (soft; DRAFT/PENDED only, 409 otherwise)
   */
  static async deleteDocument(patientId: string, authorizationId: string, docId: string): Promise<void> {
    try {
      const response: ApiResponse<void> = await apiClient.delete(
        `/patientmr/${patientId}/authorization/${authorizationId}/documents/${docId}`
      );
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to delete document');
      }
    } catch (error) {
      console.error(`Error deleting document ${docId} from authorization ${authorizationId}`);
      throw error;
    }
  }

  /**
   * Get a signed download URL for a specific document (GET …/documents/{docId})
   */
  static async getDocumentUrl(patientId: string, authorizationId: string, docId: string): Promise<string> {
    const data = await AuthorizationsService.getDocumentSignedUrl(patientId, authorizationId, docId);
    if (data?.signedUrl) return data.signedUrl;
    throw new Error('Failed to get document URL');
  }

  // ── Visit links (PA-08 retro-link) ─────────────────────────────────────

  /** Link a completed visit to this PA (consumes its units server side). */
  static async linkVisit(patientId: string, authorizationId: string, visitId: string): Promise<void> {
    const response: ApiResponse<unknown> = await apiClient.post(
      `/patientmr/${patientId}/authorization/${authorizationId}/visits/${visitId}`
    );
    if (!response.success) throw new Error(response.error?.message || 'Failed to link visit');
  }

  /** Unlink a visit from this PA (reverses its units server side). */
  static async unlinkVisit(patientId: string, authorizationId: string, visitId: string): Promise<void> {
    const response: ApiResponse<unknown> = await apiClient.delete(
      `/patientmr/${patientId}/authorization/${authorizationId}/visits/${visitId}`
    );
    if (!response.success) throw new Error(response.error?.message || 'Failed to unlink visit');
  }

  // ── PDN packet (PA-10) ─────────────────────────────────────────────────

  /** Build the merged packet PDF; 422 names the missing form/field (e.g. agency taxonomy). */
  static async generatePacket(patientId: string, authorizationId: string): Promise<PAPacketResponse> {
    const response: ApiResponse<PAPacketResponse> = await apiClient.post(
      `/patientmr/${patientId}/authorization/${authorizationId}/packet`
    );
    if (response.success && response.data?.url) {
      return {
        url: response.data.url,
        version: response.data.version,
        forms: Array.isArray(response.data.forms) ? response.data.forms : []
      };
    }
    throw new Error(response.error?.message || 'Failed to generate the PA packet');
  }

  // ── MCO list (systemConfig.payers.medicaidMcos, via the insurance settings) ──

  /** Medicaid MCOs from system config (read through the Phase 7 insurance settings). Cached per patient. */
  static async getMedicaidMcos(patientId: string): Promise<PAMcoOption[]> {
    let pending = mcoCache.get(patientId);
    if (!pending) {
      pending = InsuranceService.listCoverages(patientId).then(res => {
        const list = (res.settings?.medicaidMcos || []) as PAMcoOption[];
        return list.filter(m => m && typeof m.code === 'string' && typeof m.label === 'string');
      });
      mcoCache.set(patientId, pending);
      pending.catch(() => mcoCache.delete(patientId));
    }
    return pending;
  }

  /** MCOs offered for a Medicaid program; entries without `programs` apply to every program. */
  static filterMcosForProgram(mcos: PAMcoOption[], program: string): PAMcoOption[] {
    const norm = (v: string) => v.toLowerCase().replace(/[^a-z0-9]/g, '');
    const target = norm(program || '');
    return mcos.filter(m =>
      !Array.isArray(m.programs) || m.programs.length === 0 || m.programs.some(p => norm(p) === target)
    );
  }

  /** @internal test hook */
  static clearMcoCache(): void {
    mcoCache.clear();
  }

  // ── Error helpers ──────────────────────────────────────────────────────

  /** Gaps from a 422 FLOWSHEET_INCOMPLETE submit; null for any other error. */
  static getFlowSheetErrors(error: unknown): PAFlowSheetError[] | null {
    if (!isApiError(error)) return null;
    const code = error.domainCode || (typeof error.details?.code === 'string' ? error.details.code : undefined);
    if (code !== 'FLOWSHEET_INCOMPLETE') return null;
    const raw = error.details?.errors;
    if (!Array.isArray(raw)) return [];
    return raw
      .filter((e): e is Record<string, unknown> => !!e && typeof e === 'object')
      .map(e => ({
        day: typeof e.day === 'string' ? e.day : '',
        time: typeof e.time === 'string' ? e.time : undefined,
        code: typeof e.code === 'string' ? e.code : 'INCOMPLETE'
      }));
  }

  /** Missing source fields/forms named by a 422 (packet generation); empty when none are listed. */
  static getMissingFields(error: unknown): string[] {
    if (!isApiError(error) || !error.details) return [];
    const d = error.details;
    const list = [d.missing, d.missingFields, d.fields].find(Array.isArray) as unknown[] | undefined;
    return (list || []).map(item => {
      if (typeof item === 'string') return item;
      if (item && typeof item === 'object') {
        const o = item as Record<string, unknown>;
        const parts = [o.form, o.field].filter((p): p is string => typeof p === 'string' && p.length > 0);
        return parts.join(': ');
      }
      return '';
    }).filter(s => s.length > 0);
  }

  // ── PA Display Utilities ───────────────────────────────────────────────

  /**
   * Get PA status badge styles (Tailwind classes)
   */
  static getPAStatusBadgeStyles(status: PAStatus | string): string {
    const map: Record<string, string> = {
      [PAStatus.DRAFT]: 'bg-slate-100 text-slate-700 border-slate-200',
      [PAStatus.SUBMITTED]: 'bg-blue-50 text-blue-700 border-blue-200',
      [PAStatus.PENDED]: 'bg-amber-50 text-amber-700 border-amber-200',
      [PAStatus.APPROVED]: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      [PAStatus.DENIED]: 'bg-red-50 text-red-700 border-red-200',
      [PAStatus.EXPIRED]: 'bg-slate-50 text-slate-500 border-slate-200',
      [PAStatus.EXHAUSTED]: 'bg-orange-50 text-orange-700 border-orange-200'
    };
    return map[status] || 'bg-slate-100 text-slate-700 border-slate-200';
  }

  /**
   * Get service category badge styles
   */
  static getServiceCategoryBadgeStyles(svc: ServiceCategory | string): string {
    // Phase 20A (D-12): the category's discipline colour
    const discipline = paCategoryDiscipline(svc);
    return discipline ? getDisciplineColors(discipline).pill : 'bg-slate-50 text-slate-700 border border-slate-200';
  }


  /**
   * Get payer chip styles
   */
  static getPayerChipStyles(payerType: PAPayerType | string): string {
    return payerType === PAPayerType.MEDICAID
      ? 'bg-violet-50 text-violet-700 border-violet-200'
      : 'bg-sky-50 text-sky-700 border-sky-200';
  }

  /**
   * Convert quantity to hours for display
   */
  static toHours(quantity: number, unitType: PAUnitType | string, minutesPerUnit: number | null): number | null {
    if (unitType === PAUnitType.HOURS) return quantity;
    if (unitType === PAUnitType.VISITS || !minutesPerUnit) return null;
    const h = (quantity * minutesPerUnit) / 60;
    return Number.isInteger(h) ? h : parseFloat(h.toFixed(1));
  }

  /**
   * Format quantity with unit label and optional hour equivalent
   */
  static formatQuantity(qty: number, unitType: PAUnitType | string, minutesPerUnit: number | null, short = false): string {
    const suffixes: Record<string, string> = short
      ? { units: 'u', visits: 'v', hours: 'h' }
      : { units: ' units', visits: ' visits', hours: ' hours' };
    const label = `${qty}${suffixes[unitType] || ''}`;
    if (unitType === PAUnitType.UNITS && minutesPerUnit && !short) {
      return `${label} (${this.toHours(qty, unitType, minutesPerUnit)} hrs)`;
    }
    return label;
  }

  /**
   * Get unit label (e.g., "units", "visits", "hours")
   */
  static getUnitLabel(unitType: PAUnitType | string): string {
    const map: Record<string, string> = { units: 'units', visits: 'visits', hours: 'hours' };
    return map[unitType] || 'units';
  }

  /**
   * Get per-week label (e.g., "Units/week")
   */
  static getPerWeekLabel(unitType: PAUnitType | string): string {
    const map: Record<string, string> = { units: 'Units/week', visits: 'Visits/week', hours: 'Hours/week' };
    return map[unitType] || 'Per week';
  }

  /**
   * Get unit duration label (e.g., "15-min units")
   */
  static getDurationLabel(minutesPerUnit: number | null): string {
    if (!minutesPerUnit) return '';
    return `${minutesPerUnit}-min units`;
  }

  /**
   * Get progress bar color based on usage percentage
   */
  static getProgressBarColor(usedQuantity: number, approvedQuantity: number): string {
    if (!approvedQuantity) return 'bg-slate-300';
    const pct = Math.round((usedQuantity / approvedQuantity) * 100);
    if (pct >= 100) return 'bg-red-500';
    if (pct >= 80) return 'bg-amber-500';
    return 'bg-emerald-500';
  }

  /**
   * Calculate days remaining until approved end date
   */
  static getDaysRemaining(approvedEndDate: string | null): number | null {
    if (!approvedEndDate) return null;
    const end = new Date(approvedEndDate);
    const now = new Date();
    const diff = Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return Math.max(0, diff);
  }

  /**
   * Check if a PA record (has serviceCategory or payerType)
   */
  static isPARecord(auth: Authorization): boolean {
    return !!(auth.serviceCategory || auth.payerType);
  }
}
